<template>
  <div class="p-grid">
    <div class="p-col-12">
      <Card>
        <template #title>
          <div class="header-container">
            <h3 class="header-title">
              <strong>{{ _t('label_alarm_viewer') }}</strong>
            </h3>
            <div class="header-actions">
              <Button
                  icon="pi pi-refresh"
                  class="p-button-rounded p-button-text"
                  :loading="loading"
                  @click="loadAlarmData"
              />
              <Button
                  icon="pi pi-print"
                  class="p-button-rounded p-button-text"
                  @click="printAlarmData"
              />
            </div>
          </div>
        </template>
        <template #content>
          <TabView v-model:activeIndex="activeTabIndex" @tab-change="onTabChange">
            <TabPanel
                v-for="tab in tabs"
                :key="tab.type"
                :header="`${_t(tab.label)} (${tabCount(tab)})`"
            >
              <DataTable
                  :value="filteredData(tab.type)"
                  :loading="loading"
                  :scrollable="true"
                  scrollHeight="60vh"
                  stripedRows
                  :rowHover="true"
                  class="tight alarm-table"
                  responsiveLayout="scroll"
                  :sortField="sortField"
                  :sortOrder="sortOrder"
                  @sort="onSort"
              >
                <Column field="type" :header="_t('Type')" :sortable="true" style="max-width: 120px">
                  <template #body="{ data }">
                    <Tag
                        :style="typeSeverity(data.type) === 'custom' ? 'background-color: #82E4F5; color: #3b3b3b' : ''"
                        :severity="typeSeverity(data.type)"
                        :value="_t(tabByType(data.type)?.label)"
                        :class="data.type === 'SERVICE' ? 'tag-cyan' : ''"
                    />
                  </template>
                </Column>
                <Column field="_ts" :header="_t('label.Start_date')" :sortable="true" style="max-width: 150px">
                  <template #body="{ data }">{{ data.alarmJobStartDate }}</template>
                </Column>
                <Column field="alarmPointId" :header="_t('label_alarm_id')" :sortable="true" style="max-width: 20%" />
                <Column field="alarmPointName" :header="_t('label_Alarm_name')" :sortable="true" style="max-width: 80%" />
              </DataTable>
            </TabPanel>
          </TabView>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import { getAlarmViewer } from '../../api/alarmviewer';
import { logError } from "@/utils/appLogger";

const TABS = [
  { type: 'ALERT',   label: 'label_alarm',      countKey: 'alertCount'   },
  { type: 'TROUBLE', label: 'label_trouble',     countKey: 'troubleCount' },
  { type: 'SERVICE', label: 'label_Maintenance', countKey: 'serviceCount' },
  { type: 'ALL',     label: 'ALL',               countKey: 'allCount'     },
];

const TYPE_SEVERITY = {
  ALERT:   'danger',
  TROUBLE: 'info',
  SERVICE: 'custom',
};

const REFRESH_INTERVAL = 7000;

function parseDmyDate(str) {
  if (!str) return 0;
  const [datePart, timePart] = str.split(' / ');
  const [dd, mm, yyyy] = datePart.split('.');
  return new Date(`${yyyy}-${mm}-${dd}T${timePart}`).getTime();
}

export default {
  name: 'AlarmViewer',

  data() {
    return {
      tabs: TABS,
      alarmData: null,
      loading: false,
      activeTabIndex: 0,
      sortField: '_ts',
      sortOrder: -1,
    };
  },

  mounted() {
    const { type } = this.$route.query;
    if (type) {
      const idx = TABS.findIndex(t => t.type === type.toUpperCase());
      if (idx !== -1) this.activeTabIndex = idx;
    }
    this.loadAlarmData();
    this._refreshTimer = setInterval(() => this.loadAlarmData(true), REFRESH_INTERVAL);
  },

  beforeUnmount() {
    clearInterval(this._refreshTimer);
  },

  watch: {
    '$route.query.type'(type) {
      if (!type) return;
      const idx = TABS.findIndex(t => t.type === type.toUpperCase());
      if (idx !== -1) this.activeTabIndex = idx;
      this.loadAlarmData(true);
    },
  },

  methods: {
    tabByType(type) {
      return TABS.find(t => t.type === type);
    },

    tabCount(tab) {
      return this.alarmData?.[tab.countKey] ?? 0;
    },

    typeSeverity(type) {
      return TYPE_SEVERITY[type] ?? 'secondary';
    },

    filteredData(type) {
      const rows = this.alarmData?.tableContent;
      if (!rows) return [];
      return type === 'ALL' ? rows : rows.filter(r => r.type === type);
    },

    onTabChange({ index }) {
      this.$router.push({ query: { type: TABS[index].type, token: this.$route.query.token } });
    },

    onSort({ sortField, sortOrder }) {
      this.sortField = sortField;
      this.sortOrder = sortOrder;
    },

    async loadAlarmData(silent = false) {
      if (!silent) this.loading = true;
      try {
        const { type = 'ALL', token = null } = this.$route.query;
        const { data } = await getAlarmViewer(type, token);
        data.tableContent?.forEach(row => { row._ts = parseDmyDate(row.alarmJobStartDate); });
        this.alarmData = data;
      } catch (error) {
        logError('Error loading alarm data', error);
        this.$toast.add({
          severity: 'error',
          summary: this._t('label_error'),
          detail: this._t('label_error_loading_data'),
          life: 3000,
          closable: false,
        });
      } finally {
        this.loading = false;
      }
    },

    printAlarmData() {
      const { type = 'ALL', token = null } = this.$route.query;
      const base = localStorage.getItem('serverPath') || process.env.VUE_APP_BASE_URL;
      window.open(`${base}${process.env.VUE_APP_BASE_API}/alarmviewer/print?type=${type}&token=${token}`, '_blank');
    },
  },
};
</script>

<style scoped>
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-title {
  margin: 0;
  flex: 1;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.alarm-table :deep(.p-datatable-table) {
  table-layout: fixed;
}

.alarm-table :deep(.p-column-title) {
  white-space: nowrap;
}

.alarm-table :deep(.p-datatable-tbody > tr > td) {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}
</style>

<style>
.p-datatable .p-datatable-tbody > tr > td {
  padding: 4px 0 !important;
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
}
</style>
